import { Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { LwButton, Page, Toolbox } from 'redesign';
import { getWorkerFullName } from 'shared/utils/worker';
import { RenderUnsettledUI } from 'shared/components';
import { useCompanyRelativePath } from 'ogp/hooks';
import { routes } from 'ogp/Routes';
import { IconEdit, IconMail, IconTrash } from 'assets/img';
import { usePageProvider } from 'ogp/layouts';
import { useDialog } from 'ogp/providers/dialog-provider';
import { invariant } from 'shared/utils/utils';
import { useAlert, userAlerts } from 'shared/components/alerts';
import { useTranslation } from 'react-i18next';
import { useIsOgpManager } from 'ogp/hooks/roles/use-is-ogp-manager';
import { EmployeeDetailHeader } from './employee-detail-header';
import { EmployeeDetailTabs } from './employee-detail-tabs';
import { useGetEmployeeDetail } from '../../hooks/use-get-employee-detail';
import { useArchiveEmployee } from '../../hooks/use-archive-employee';
import { useResendEmployeeTemporaryPassword } from '../../hooks/use-resend-employee-temporary-password';

export const EmployeeDetail = () => {
  const { id } = useParams<'id'>();
  const isOgpManager = useIsOgpManager();
  const employeeQuery = useGetEmployeeDetail(id ?? '');
  const generatePath = useCompanyRelativePath();

  const [{ filters }] = usePageProvider();
  const { alertError, alertSuccess } = useAlert();
  const { openDialog, closeDialog, disableDialog } = useDialog();

  const archiveEmployeeMutation = useArchiveEmployee();
  const navigate = useNavigate();
  const { t } = useTranslation(['web-common', 'ogp-screen-specific']);

  const resendTemporaryPasswordMutation = useResendEmployeeTemporaryPassword();

  if (employeeQuery.status !== 'success') {
    return <RenderUnsettledUI data={employeeQuery} />;
  }

  const handleArchiveEmployee = () => {
    invariant(id, 'Employee is not defined');
    disableDialog();
    archiveEmployeeMutation.mutate(id, {
      onSuccess: () => {
        navigate(generatePath(routes.Workforce));
        closeDialog();
        alertSuccess(
          t('employee_detail_screen.archive_employee_success', { ns: 'ogp-screen-specific' })
        );
      },
      onError: (e) => {
        closeDialog();
        alertError(e);
      },
    });
  };

  const handleArchival = () => {
    openDialog(
      { title: t('employee_detail_screen.archive_employee_dialog', { ns: 'ogp-screen-specific' }) },
      handleArchiveEmployee
    );
  };

  const handleResendTemporaryPassword = () => {
    resendTemporaryPasswordMutation.mutate(employeeQuery.data.email, {
      onSuccess: () => alertSuccess(userAlerts.success.resendTempCredentials),
      onError: (e) => alertError(e),
    });
  };

  const isOnboarded = employeeQuery.data.isOnboarded;
  const allowResendTemporaryPassword = employeeQuery.data.allowResendTemporaryPassword;
  const labourEngagementState = employeeQuery.data.labourEngagementState;

  return (
    <Page
      header={
        <EmployeeDetailHeader
          name={getWorkerFullName(employeeQuery.data)}
          profilePictureUrl={employeeQuery.data.profilePictureUrl}
          isOnboarded={isOnboarded}
          labourEngagementState={labourEngagementState}
        />
      }
      toolbox={
        <Toolbox>
          <LwButton to={generatePath(routes.Workforce)} type="submit" color="secondary">
            {t('buttons.back', { ns: 'web-common' })}
          </LwButton>
          <LwButton
            color="primary"
            disabled={!isOgpManager}
            iconColorMode="fill"
            startIcon={<IconEdit />}
            to={generatePath(routes.EmployeeUpdate, { id }, filters)}
          >
            {t('employee_detail_screen.update_employee_button', { ns: 'ogp-screen-specific' })}
          </LwButton>

          {allowResendTemporaryPassword ? (
            <LwButton
              color="secondary"
              startIcon={<IconMail />}
              onClick={handleResendTemporaryPassword}
            >
              {t('buttons.resend_temp_credentials', { ns: 'web-common' })}
            </LwButton>
          ) : null}

          <LwButton
            onClick={handleArchival}
            startIcon={<IconTrash />}
            iconColorMode="stroke"
            color="secondary"
            disabled={!isOgpManager}
          >
            {t('buttons.delete', { ns: 'web-common' })}
          </LwButton>
        </Toolbox>
      }
    >
      <Box width="100%">
        <EmployeeDetailTabs data={employeeQuery.data} />
      </Box>
    </Page>
  );
};
